import { PointArray } from '@editor/interfaces/attribute'
import { isNewPointOverlapping } from '@editor/services/Points'
import { PayloadAction, createSlice } from '@reduxjs/toolkit'

import { addPolylineWidths, deletePolylines, updatePolylines } from './thunks'

export const TEMP_ID_PREFIX = 'temp_polyline'
export const WORKING_ID_PREFIX = 'working_polyline'

export interface PolylineData {
  /**
   * Unique identifier for the polyline.
   */
  id: string

  /**
   * If the polyline is associated with a job, this holds the job ID (job token).
   */
  jobId?: string

  /**
   * If the polyline is derived from an existing inspection item, this holds its ID.
   */
  inspectionItemId?: string

  /**
   * Optional group ID if the polyline is part of a group.
   */
  groupId?: string

  /**
   * Points that make up the polyline.
   */
  points: PointArray[]

  /**
   * Optional width of the polyline.
   */
  width?: number

  /**
   * Flag to indicate if the polyline is marked for deletion.
   */
  isDeleted?: boolean
}

export interface PolylineState {
  /**
   * Flag to determine if the polylines can be saved.
   * @deprecated Need to convert to custom createSelector.
   */
  isSaveable: boolean

  /**
   * Flag to determine if the polyline tool is in closing mode.
   */
  isClosing: boolean

  /**
   * Points of the currently working polyline.
   */
  workingPoints: PointArray[]

  /**
   * Width of polyline during creation.
   */
  workingWidth?: number

  /**
   * The current working point falling within which crack detection ID, if any.
   */
  workingPointCrackDetectionId?: string

  /**
   * Completed drawing polylines.
   */
  completedPolylines: PolylineData[]

  /**
   * Saved polylines that have been being edited.
   */
  editedPolylines: PolylineData[]

  /**
   * A copy of completed polylines that are currently being saved.
   * This is used to prevent duplicate saves when the user tries to save multiple times.
   */
  savingPolylines: PolylineData[]

  /**
   * Polylines that are currently being updated.
   */
  updatingPolylines: string[]

  /**
   * Label visibility settings while the tool is active.
   * These are only active while the tool is selected.
   */
  inToolLabelVisibility: {
    primary: boolean
    edge: boolean
  }
}

const initialState: PolylineState = {
  isSaveable: false,
  isClosing: false,
  workingPoints: [],
  completedPolylines: [],
  editedPolylines: [],
  savingPolylines: [],
  updatingPolylines: [],
  inToolLabelVisibility: {
    // These default values match DefaultEditorDisplayConfig.
    // Actual values are initialized from polylineDisplaySettings when the tool is selected (see useEditor.ts).
    primary: true,
    edge: false,
  },
}

const upsertPolyline = (state: PolylineState, payload: PolylineData) => {
  const index = state.editedPolylines.findIndex((polyline) => polyline.id === payload.id)
  if (index !== -1) {
    state.editedPolylines[index] = payload
  } else {
    state.editedPolylines.push(payload)
  }
}

export const slice = createSlice({
  name: 'editor/tools/polyline/polyline',
  initialState,
  reducers: {
    // ## Editor states
    setIsSaveable(state, action: PayloadAction<boolean>) {
      state.isSaveable = action.payload
    },

    // ## Working points
    addWorkingPoint: (state, { payload }: PayloadAction<PointArray>) => {
      // Filter out points that are too close to existing points
      if (!isNewPointOverlapping(state.workingPoints, payload)) {
        state.workingPoints.push(payload)
      }
      state.isSaveable = false
    },
    setWorkingCrackDetectionId: (state, action: PayloadAction<string | undefined>) => {
      state.workingPointCrackDetectionId = action.payload
    },

    // ## Working polyline
    removeWorkingPolyline: (state) => {
      state.workingPoints = []
      state.isSaveable = true
    },
    undoLastWorkingPolyline: (state) => {
      if (state.workingPoints.length > 0) {
        state.workingPoints.pop()
      } else if (state.completedPolylines.length > 0) {
        const lastPolyline = state.completedPolylines[state.completedPolylines.length - 1]
        state.completedPolylines.pop()
        state.workingPoints = lastPolyline.points.slice()
      }

      state.isSaveable = state.workingPoints.length === 0 && state.completedPolylines.length > 0
    },
    setWorkingWidth: (state, action: PayloadAction<number | undefined>) => {
      state.workingWidth = action.payload
    },

    /**
     * -- Completed Polyline
     */
    setCompletedPolylines: (state, action: PayloadAction<PolylineData[]>) => {
      state.completedPolylines = action.payload
    },
    addCompletedPolyline: (state, action: PayloadAction<PolylineData>) => {
      state.completedPolylines.push({
        ...action.payload,
        jobId: state.workingPointCrackDetectionId,
      })
      state.workingPointCrackDetectionId = undefined
      state.isSaveable = true
    },
    addCompletedPolylines: (state, action: PayloadAction<PolylineData[]>) => {
      state.completedPolylines.push(...action.payload)
      state.isSaveable = true
    },
    updateCompletedPolyline: (state, { payload: { id, points, width, jobId } }: PayloadAction<PolylineData>) => {
      const poly = state.completedPolylines.find((polyline) => polyline.id === id)
      if (poly) {
        poly.points = points
        poly.width = width ?? poly.width
        poly.jobId = jobId
      }
    },
    updateCompletedPolylineWidths: (
      state,
      { payload: { ids, width } }: PayloadAction<{ ids: string[]; width?: number }>,
    ) => {
      state.completedPolylines.forEach((polyline) => {
        if (ids.includes(polyline.id) || (polyline.inspectionItemId && ids.includes(polyline.inspectionItemId))) {
          polyline.width = width
        }
      })
    },
    deleteCompletedPolylines: (state, action: PayloadAction<string[]>) => {
      state.completedPolylines = state.completedPolylines.filter((polyline) => !action.payload.includes(polyline.id))
    },
    deleteCompletedPolylinesByJobId: (state, action: PayloadAction<string>) => {
      state.completedPolylines = state.completedPolylines.filter((polyline) => polyline.jobId !== action.payload)
    },
    resetCompletedPolylines: (state) => {
      state.completedPolylines = []
    },

    /**
     * -- Edited polylines
     */
    // Set or update edited polyline
    upsertEditedPolyline: (state, { payload }: PayloadAction<PolylineData>) => {
      upsertPolyline(state, payload)
      state.isSaveable = true
    },
    upsertEditedPolylines: (state, { payload }: PayloadAction<PolylineData[]>) => {
      payload.forEach((polyline) => upsertPolyline(state, polyline))
      state.isSaveable = true
    },
    clearEditedPolylines: (state, { payload: ids }: PayloadAction<string[]>) => {
      state.editedPolylines = state.editedPolylines.filter((polyline) => !ids.includes(polyline.id))

      // Remove any completed polylines that correspond to the cleared edited polylines
      state.completedPolylines = state.completedPolylines.filter(
        (polyline) =>
          !polyline.inspectionItemId || (polyline.inspectionItemId && !ids.includes(polyline.inspectionItemId)),
      )
    },
    resetEditedPolylines: (state) => {
      state.editedPolylines = []
    },

    /**
     * Merge two polylines.
     */
    mergePolylines: (
      state,
      {
        payload: { firstPolyline, secondPolyline, newPoints },
      }: PayloadAction<{ firstPolyline: PolylineData; secondPolyline: PolylineData; newPoints: PointArray[] }>,
    ) => {
      // When merging with saved polylines, we need to add completed into saved, so we need to figure out which is which
      // If merging two saved polylines, it will be treated the same as completed polylines (second into first, then the second removed)
      const savedPolyline = [firstPolyline, secondPolyline].find(({ id, inspectionItemId }) => id === inspectionItemId)
      const savedPolylineItemId = savedPolyline?.inspectionItemId
      const width = firstPolyline.width ?? secondPolyline.width

      // No polyline ID means both are completed polylines
      if (!savedPolylineItemId) {
        // -- Remove the two polylines being merged
        state.completedPolylines = state.completedPolylines.filter(
          (polyline) => polyline.id !== firstPolyline.id && polyline.id !== secondPolyline.id,
        )

        // -- Add the new merged polyline
        state.completedPolylines.push({
          id: crypto.randomUUID(),
          points: newPoints,
          width,
        })
      } else {
        const isFirstSaved = firstPolyline.id === savedPolyline?.id
        const otherPolyline = isFirstSaved ? secondPolyline : firstPolyline

        // -- Upsert the saved polyline
        upsertPolyline(state, {
          id: savedPolyline.id,
          inspectionItemId: savedPolylineItemId,
          groupId: savedPolyline.groupId,
          points: newPoints,
          width,
        })

        // -- Remove the second polyline. It can be either completed or saved.
        if (
          firstPolyline.id === firstPolyline.inspectionItemId &&
          secondPolyline.id === secondPolyline.inspectionItemId
        ) {
          // Both are saved polylines
          upsertPolyline(state, {
            id: secondPolyline.id,
            inspectionItemId: secondPolyline.inspectionItemId,
            groupId: secondPolyline.groupId,
            points: [],
            isDeleted: true,
          })
        } else {
          // Second is completed polyline
          state.completedPolylines = state.completedPolylines.filter((polyline) => polyline.id !== otherPolyline.id)
        }
      }
    },

    /**
     * -- Saving polylines
     */
    // Copy over completed polylines to saving polylines by merging the existing ones.
    copySavingPolylines: (state) => {
      state.savingPolylines.push(...state.completedPolylines)
      state.completedPolylines = []
    },
    // Restore saving polylines back to completed polylines. Generally used when saving fails.
    restoreSavingPolylines: (state) => {
      state.completedPolylines.push(...state.savingPolylines)
      state.savingPolylines = []
    },
    // Clear saving polylines by the provided IDs.
    clearSavedPolylines: (state, { payload }: PayloadAction<string[]>) => {
      state.savingPolylines = state.savingPolylines.filter(({ id }) => !payload.includes(id))
    },

    /**
     * -- In-tool label visibility
     */
    setInToolLabelVisibility(state, action: PayloadAction<{ primary?: boolean; edge?: boolean }>) {
      if (action.payload.primary !== undefined) {
        state.inToolLabelVisibility.primary = action.payload.primary
      }
      if (action.payload.edge !== undefined) {
        state.inToolLabelVisibility.edge = action.payload.edge
      }
    },

    // Reset
    reset: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      /**
       * When updating polylines, set their ID as updating.
       */
      .addCase(updatePolylines.pending, (state, action) => {
        state.updatingPolylines.push(
          ...action.meta.arg.items.map((item) => item.polyline_length_id || item.inspection_item_id!),
        )
      })
      .addCase(updatePolylines.fulfilled, (state, action) => {
        state.updatingPolylines = state.updatingPolylines.filter(
          (id) =>
            !action.meta.arg.items.map((item) => item.polyline_length_id || item.inspection_item_id!).includes(id),
        )
      })
      .addCase(updatePolylines.rejected, (state, action) => {
        state.updatingPolylines = state.updatingPolylines.filter(
          (id) =>
            !action.meta.arg.items.map((item) => item.polyline_length_id || item.inspection_item_id!).includes(id),
        )
      })

      /**
       * When deleting polylines, set their ID as updating.
       */
      .addCase(deletePolylines.pending, (state, action) => {
        state.updatingPolylines.push(...action.meta.arg.items)
      })
      .addCase(deletePolylines.fulfilled, (state, action) => {
        state.updatingPolylines = state.updatingPolylines.filter((id) => !action.meta.arg.items.includes(id))
      })
      .addCase(deletePolylines.rejected, (state, action) => {
        state.updatingPolylines = state.updatingPolylines.filter((id) => !action.meta.arg.items.includes(id))
      })

      /**
       * When adding polyline widths, set their parent ID as updating.
       */
      .addCase(addPolylineWidths.pending, (state, action) => {
        state.updatingPolylines.push(...action.meta.arg.items.map((item) => item.polyline_length_id!))
      })
      .addCase(addPolylineWidths.fulfilled, (state, action) => {
        state.updatingPolylines = state.updatingPolylines.filter(
          (id) => !action.meta.arg.items.map((item) => item.polyline_length_id!).includes(id),
        )
      })
      .addCase(addPolylineWidths.rejected, (state, action) => {
        state.updatingPolylines = state.updatingPolylines.filter(
          (id) => !action.meta.arg.items.map((item) => item.polyline_length_id!).includes(id),
        )
      })
  },
})

export const {
  // Working points
  addWorkingPoint,
  setWorkingCrackDetectionId,

  // Working polyline
  removeWorkingPolyline,
  undoLastWorkingPolyline,
  setWorkingWidth,

  // Competed working Polyline
  setCompletedPolylines,
  addCompletedPolyline,
  addCompletedPolylines,
  updateCompletedPolyline,
  updateCompletedPolylineWidths,
  mergePolylines,
  deleteCompletedPolylines,
  deleteCompletedPolylinesByJobId,
  resetCompletedPolylines,

  // Edited polylines
  upsertEditedPolyline,
  upsertEditedPolylines,
  clearEditedPolylines,
  resetEditedPolylines,

  // Saving polylines
  copySavingPolylines,
  restoreSavingPolylines,
  clearSavedPolylines,

  // In-tool label visibility
  setInToolLabelVisibility,

  // Reset
  // reset,
} = slice.actions

export default slice.reducer
